import { useField, useFormikContext } from 'formik'
import React, { CSSProperties, useEffect, useMemo, useState } from 'react'

import FieldError from '@/components/form/FieldError'
import FieldLabel from '@/components/form/FieldLabel'
import { useIsVerySmallDevice } from '@/hooks/useIsVerySmallDevice'
import { MONTH_OPTION_LABELS } from '@/lib/constants'
import { isDevelopment } from '@/lib/helpers/envHelpers'
import { getCurrentAgeFromDob } from '@/lib/helpers/helperFunctions'

interface Props {
  name: string
  label?: string
  maxAge?: number
  isDisabled?: boolean
  /** Autofocus the day select on mount. */
  autoFocus?: boolean
}

const DateOfBirthInput: React.FC<Props> = ({
  name,
  label,
  maxAge = 120,
  isDisabled,
  autoFocus,
}) => {
  const { submitCount, setFieldValue } = useFormikContext()
  const [field, { error }] = useField(name)
  const id = `field_${name}`
  const showError = !!(submitCount > 0 && error)
  const isVerySmallDevice = useIsVerySmallDevice()

  const dates = useMemo(() => {
    const values = (field.value || '').split('/')

    return {
      day: values[0] || '',
      month: values[1] || '',
      year: values[2] || '',
    }
  }, [field.value])

  const [selectedDay, setSelectedDay] = useState(dates.day)
  const [selectedMonth, setSelectedMonth] = useState(dates.month)
  const [selectedYear, setSelectedYear] = useState(dates.year)

  useEffect(() => {
    const newValue = `${selectedDay}/${selectedMonth}/${selectedYear}`

    // If no date of birth parts are set, set the value to an empty string
    setFieldValue(name, newValue === '//' ? '' : newValue)
  }, [name, selectedDay, selectedMonth, selectedYear, setFieldValue])

  useEffect(() => {
    setSelectedDay(dates.day)
    setSelectedMonth(dates.month)
    setSelectedYear(dates.year)
  }, [dates.day, dates.month, dates.year, field.value])

  const shouldShowAgeNextToYear = isDevelopment() && !isVerySmallDevice

  function handleChange(
    fieldName: string,
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    field.onChange(event)
    const value = event.target.value

    switch (fieldName) {
      case 'day':
        setSelectedDay(value)
        break
      case 'month':
        setSelectedMonth(value)
        break
      case 'year':
        setSelectedYear(value)
    }
  }

  const selectControlStyles: CSSProperties = {
    width: '80px',
  }

  const dayOptions = useMemo(() => {
    return new Array(31).fill(undefined).map((_, index) => {
      const day = index + 1
      const label = String(day).padStart(2, '0')

      return (
        <option key={day} value={label}>
          {label}
        </option>
      )
    })
  }, [])

  const monthOptions = useMemo(() => {
    return new Array(12).fill(undefined).map((_, index) => {
      const month = index + 1
      const label = MONTH_OPTION_LABELS[index]
      const value = String(month).padStart(2, '0')

      return (
        <option key={label} value={value}>
          {label}
        </option>
      )
    })
  }, [])

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const minAge = 16
    const startingYear = currentYear - minAge

    return new Array(maxAge - minAge).fill(undefined).map((_, index) => {
      const year = startingYear - index

      // TODO: clean this up
      const dob = `${selectedDay || 1}/${selectedMonth || 1}/${year}`
      const age = getCurrentAgeFromDob(dob)
      const label =
        shouldShowAgeNextToYear && typeof age !== 'undefined'
          ? `${year} (${age})`
          : year

      return (
        <option key={year} value={year.toString()}>
          {label}
        </option>
      )
    })
  }, [maxAge, selectedDay, selectedMonth, shouldShowAgeNextToYear])

  return (
    <>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}

      <div className="flex flex-wrap gap-2">
        <select
          value={selectedDay}
          onChange={(event) => handleChange('day', event)}
          className="select"
          style={selectControlStyles}
          disabled={isDisabled}
          autoFocus={autoFocus}
        >
          <option value="">DD</option>
          {dayOptions}
        </select>

        <select
          value={selectedMonth}
          onChange={(event) => handleChange('month', event)}
          className="select"
          style={selectControlStyles}
          disabled={isDisabled}
        >
          <option value="">MMM</option>
          {monthOptions}
        </select>

        <select
          value={selectedYear}
          onChange={(event) => handleChange('year', event)}
          className="select"
          style={{
            ...selectControlStyles,
            width: shouldShowAgeNextToYear
              ? '110px'
              : selectControlStyles.width,
          }}
          disabled={isDisabled}
        >
          <option value="">YYYY</option>
          {yearOptions}
        </select>
      </div>

      {showError && <FieldError inputId={id}>{error}</FieldError>}
    </>
  )
}

export default DateOfBirthInput
